<template>
  <div class="contain callpop-stores">
    <div class="callpop-stores__head">
      <h3 class="callpop-stores__title">{{ title }}</h3>
      <div class="callpop-stores__actions">
        <el-button v-if="can.storeAdd" size="mini" type="primary" plain icon="el-icon-plus" @click="goRegister">{{
          text.storeRegisterAction
        }}</el-button>
        <el-button v-if="can.autoJoinRun" size="mini" icon="el-icon-refresh" @click="confirmAutoJoinRun">{{
          text.autoJoinRunAction
        }}</el-button>
        <el-button
          v-if="can.joinBulk"
          size="mini"
          type="warning"
          plain
          :disabled="checked.length === 0"
          @click="confirmJoinBulk"
          >{{ text.joinBulkAction }}</el-button
        >
      </div>
    </div>

    <!-- 필터 — 범위는 서버가 좁힌다. 여기 있는 것은 사람이 더 좁히는 축뿐이다 -->
    <el-form class="callpop-stores__filter" size="mini" inline>
      <el-form-item :label="text.operatorLabel">
        <el-select
          v-model="searchForm.operatorCd"
          class="callpop-stores__operator"
          clearable
          filterable
          :disabled="!!operatorsDenied"
          :placeholder="operatorsDenied || text.operatorLabel"
          @change="onOperatorFilter">
          <el-option
            v-for="row in operatorOptions"
            :key="row.id"
            :label="row.label"
            :value="row.operatorCd"></el-option>
        </el-select>
      </el-form-item>
      <el-form-item :label="text.agencyLabel">
        <AgencyPicker
          v-model="agencyId"
          class="callpop-stores__agency"
          :operator-cd="searchForm.operatorCd"
          :placeholder="text.agencyLabel"
          @change="onAgencyFilter" />
      </el-form-item>
      <el-form-item>
        <el-input
          v-model="searchForm.q"
          class="callpop-stores__q"
          clearable
          :placeholder="text.searchPlaceholder"
          @keyup.enter.native="searchFirstPage" />
      </el-form-item>
      <el-form-item :label="text.participationLabel">
        <el-select v-model="searchForm.participation" class="callpop-stores__small" clearable @change="searchFirstPage">
          <el-option
            v-for="row in participationFilters"
            :key="row.value"
            :label="row.label"
            :value="row.value"></el-option>
        </el-select>
      </el-form-item>
      <el-form-item :label="text.eligibilityFilterLabel">
        <el-select v-model="searchForm.eligibility" class="callpop-stores__small" clearable @change="searchFirstPage">
          <el-option :label="text.eligibilityEligible" value="ELIGIBLE"></el-option>
          <el-option :label="text.eligibilityIneligible" value="INELIGIBLE"></el-option>
        </el-select>
      </el-form-item>
      <!-- 실적 기간 — 행을 좁히지 않고 콜팝 주문 두 칸의 기간만 정한다. 비울 수 없다(비면 셀 기간이 없다) -->
      <el-form-item :label="text.ordersPeriodLabel">
        <el-date-picker
          v-model="ordersPeriod"
          class="callpop-stores__period"
          type="daterange"
          value-format="yyyy-MM-dd"
          range-separator="~"
          :clearable="false"
          unlink-panels
          @change="search" />
      </el-form-item>
      <el-form-item>
        <el-button size="mini" icon="el-icon-search" @click="searchFirstPage">{{ text.searchAction }}</el-button>
      </el-form-item>
    </el-form>

    <el-alert v-if="denied" :title="denied" type="info" :closable="false" show-icon />

    <template v-else>
      <el-table
        ref="storeTable"
        :data="rows"
        size="mini"
        border
        v-loading="loading"
        :empty-text="text.storeEmpty"
        @selection-change="onSelectionChange">
        <el-table-column v-if="can.joinBulk" type="selection" width="42" align="center"></el-table-column>
        <!--
          매장 칸은 **매장을 가리키는 것 전부**다 — 사업자번호·주소를 제 열로 두면 1440 에서 오른쪽이
          고정 「관리」 열에 가린다(§2-5). 열을 없애지 않고 한 칸 안에 접었고, 긴 주소는 잘라 `title` 로 남긴다.
        -->
        <el-table-column :label="text.storeNmColumn" min-width="158">
          <template slot-scope="scope">
            <!--
              관리점·운영사의 매장 운영 대행(R-E2) — 매장명이 사업장 관리자의 「매장 관리」로 들어가는 링크다(새 화면이
              아니다). 들어갈 수 있는지는 서버 액션이 답한다([canEnterStoreOps]) — 링크가 없는 세션은 종전 그대로 글자다
            -->
            <router-link
              v-if="can.storeOps"
              class="callpop-stores__store-link"
              :to="scope.row.storeOpsRoute"
              :title="storeOpsText.openStoreOps"
              >{{ scope.row.storeNm }}</router-link
            >
            <div v-else>{{ scope.row.storeNm }}</div>
            <div class="callpop-stores__sub">
              {{ scope.row.storeId }}
              <el-tooltip v-if="scope.row.inactive" placement="top" :content="text.storeInactiveTip">
                <el-tag size="mini" type="danger">{{ text.storeInactive }}</el-tag>
              </el-tooltip>
            </div>
            <div v-if="scope.row.busiNumText" class="callpop-stores__sub callpop-stores__keep">
              {{ scope.row.busiNumText }}
            </div>
            <div
              v-if="scope.row.storeAddr"
              class="callpop-stores__sub callpop-stores__clip"
              :title="scope.row.addrTitle">
              {{ scope.row.storeAddr }}
            </div>
          </template>
        </el-table-column>
        <el-table-column :label="text.ownerColumn" min-width="130">
          <template slot-scope="scope">
            <!-- 「이름 (코드)」는 코드가 쪼개지면 못 읽는다 — 1280 실측에서 「(BG0 / 001)」이 나왔다 -->
            <div class="callpop-stores__keep">{{ scope.row.owner.operatorText }}</div>
            <div class="callpop-stores__sub callpop-stores__keep">{{ scope.row.owner.agencyText }}</div>
            <!-- 매핑일은 소속이 언제 정해졌는지라 소속 칸에 산다. 시각까지는 `title` 에 남는다 -->
            <div class="callpop-stores__sub callpop-stores__stamp" :title="scope.row.mappedAt">
              {{ text.mappedAtColumn }} {{ scope.row.mappedAtDate }}
            </div>
          </template>
        </el-table-column>
        <el-table-column :label="text.participationLabel" width="100">
          <template slot-scope="scope">
            <el-tag size="mini" :type="scope.row.participationChip.tagType">{{
              scope.row.participationChip.text
            }}</el-tag>
            <div v-if="scope.row.participationSource" class="callpop-stores__sub callpop-stores__keep">
              {{ scope.row.participationSource }}
            </div>
            <!--
              앱 노출(11-03)도 참여와 다른 축이다 — 「참여 중」은 참여 등록일 뿐이고, 앱에는 콜팝 플레이스에 편성된 매장만 보인다.
              참여 중인데 편성이 없는(참여 조건 미달 자동 제외로 빠진) 매장만 색으로 드러낸다
            -->
            <div
              class="callpop-stores__sub callpop-stores__keep"
              :class="{ 'callpop-stores__app-warn': scope.row.app.warn }"
              :title="text.appPlaceTitle">
              {{ text.appPlaceLabel }} {{ scope.row.app.text }}
            </div>
            <!-- TV 입점은 참여와 **다른 축**이라 낱말을 앞에 달고 참여 칸의 보조 표기로 선다(§2-5) -->
            <div class="callpop-stores__sub callpop-stores__keep">{{ text.tvColumn }} {{ scope.row.tvText }}</div>
            <!--
              콜팝 주문 실적(R-V A) — 실적 기간 안의 유효 주문 건수·결제 금액. 두 수도 TV 입점처럼 낱말을 단 보조 줄이다:
              제 열로 세우면 1280 에서 표가 「관리」 열 아래로 넘친다(표 폭 시험 — 1280 의 여유가 8px 뿐이다)
            -->
            <div class="callpop-stores__sub callpop-stores__keep callpop-stores__orders-count">
              {{ text.ordersCountLabel }} {{ scope.row.orders.countText }}
            </div>
            <div class="callpop-stores__sub callpop-stores__keep callpop-stores__orders-amount">
              {{ text.ordersAmountLabel }} {{ scope.row.orders.amountText }}
            </div>
          </template>
        </el-table-column>
        <!-- 날짜와 시각을 **화면이 정한 자리**에서 끊는다(`stampView`) — 한 문자열은 좁은 칸에 못 들어간다 -->
        <el-table-column :label="text.regDtmColumn" width="88">
          <template slot-scope="scope">
            <div class="callpop-stores__stamp">{{ scope.row.regDtm.date }}</div>
            <div class="callpop-stores__sub callpop-stores__stamp">{{ scope.row.regDtm.time }}</div>
          </template>
        </el-table-column>
        <el-table-column :label="text.joinedAtColumn" width="88">
          <template slot-scope="scope">
            <div class="callpop-stores__stamp">{{ scope.row.joinedAt.date }}</div>
            <div class="callpop-stores__sub callpop-stores__stamp">{{ scope.row.joinedAt.time }}</div>
          </template>
        </el-table-column>
        <el-table-column v-if="can.pgContract" :label="text.pgContractColumn" width="88">
          <template slot-scope="scope">
            <div class="callpop-stores__stamp">{{ scope.row.pgContractCompletedDt || '-' }}</div>
          </template>
        </el-table-column>
        <el-table-column :label="text.eligibilityColumn" min-width="118">
          <template slot-scope="scope">
            <!-- 사유 하나가 태그 하나다 — 이어 붙이면 `.el-tag` 의 `nowrap` 이 칸 밖을 잘라 버린다 -->
            <div v-for="(tag, index) in scope.row.eligibility" :key="index" class="callpop-stores__reason">
              <el-tag size="mini" :type="tag.tagType">{{ tag.text }}</el-tag>
            </div>
            <span v-if="scope.row.eligibility.length === 0">-</span>
          </template>
        </el-table-column>
        <el-table-column :label="$t('관리')" width="150" fixed="right">
          <template slot-scope="scope">
            <!--
              버튼 사이 간격을 **셀이** 소유한다. Element 의 `.el-button + .el-button{margin-left:10px}` 은
              **형제가 버튼일 때만** 걸리는데, 참여 등록은 툴팁의 `<span>` 안에 있어 그 규칙 밖이었다 —
              1440 dev 실측에서 「이력참여 등록」으로 붙어 보였다. 줄이 모자라면 다음 줄로 접힌다.
            -->
            <div class="callpop-stores__row-actions">
              <el-button v-if="can.mapping" size="mini" type="text" @click="openMapping(scope.row.raw)">{{
                scope.row.owner.mapped ? text.transferMappingAction : text.mappingAction
              }}</el-button>
              <el-button v-if="can.mappingLog" size="mini" type="text" @click="openHistory(scope.row.raw)">{{
                text.historyAction
              }}</el-button>
              <el-button v-if="can.pgContract" size="mini" type="text" @click="openPgContract(scope.row.raw)">{{
                text.pgContractAction
              }}</el-button>
              <!-- 조건 미달이면 **끈 채로 사유를 말한다** — 사라진 버튼은 「이 화면에 그 기능이 없다」로 읽힌다 -->
              <el-tooltip
                v-if="can.join && scope.row.joinable"
                placement="top"
                :disabled="!scope.row.joinLock"
                :content="scope.row.joinLock || ''">
                <span>
                  <el-button
                    size="mini"
                    type="text"
                    :disabled="!!scope.row.joinLock"
                    @click="confirmJoin(scope.row.raw)"
                    >{{ joinActionText }}</el-button
                  >
                </span>
              </el-tooltip>
              <!--
                KT 우리동네 가입 여부 조회([checkMytown]) — 판정이 사업자번호 단위라 답을 받으면 목록을 다시 읽는다.
                요청 중에는 **이 행의 버튼만** 잠근다(버튼 로딩 — 전역 잠금 로딩은 켜지 않는다)
              -->
              <el-button
                v-if="can.mytownCheck"
                size="mini"
                type="text"
                :loading="checkingStoreCds.includes(scope.row.storeCd)"
                @click="checkMytown(scope.row.raw)"
                >{{ tvCheckActionText }}</el-button
              >
            </div>
          </template>
        </el-table-column>
      </el-table>

      <list-pagination
        :search-form="searchForm"
        :total="totalCount"
        :page-sizes="pageSizes"
        @page-change="onPageChange"
        @size-change="onSizeChange" />
    </template>

    <StoreMappingDialog
      :visible.sync="mappingVisible"
      :store="activeStore"
      :operators="operatorRows"
      @saved="afterMappingSaved" />

    <StoreJoinBulkResult :visible.sync="bulkVisible" :summary="bulkSummary" />

    <!-- 매장 한 곳의 매핑 이력 — 화면 #4 와 **같은 표**를 쓴다 -->
    <el-dialog :title="text.historyTitle" :visible.sync="historyVisible" width="820px">
      <OrgHistoryTable
        :items="historyItems"
        :refs="historyRefs"
        :loading="historyLoading"
        :show-target="false"
        max-height="420" />
      <span slot="footer">
        <el-button size="small" @click="historyVisible = false">{{ text.closeAction }}</el-button>
      </span>
    </el-dialog>

    <el-dialog :title="text.pgContractTitle" :visible.sync="pgVisible" width="420px">
      <el-date-picker
        v-model="pgContractDt"
        class="callpop-stores__pg"
        type="date"
        value-format="yyyy-MM-dd"
        :placeholder="text.pgContractColumn" />
      <p class="callpop-stores__hint">{{ text.pgContractHint }}</p>
      <span slot="footer">
        <el-button size="small" @click="pgVisible = false">{{ text.cancelAction }}</el-button>
        <el-button size="small" type="primary" :loading="pgSaving" @click="savePgContract">{{
          text.saveAction
        }}</el-button>
      </span>
    </el-dialog>
  </div>
</template>

<script>
import AgencyPicker from '@/components/callpop/AgencyPicker.vue'
import OrgHistoryTable from '@/components/callpop/OrgHistoryTable.vue'
import StoreJoinBulkResult from '@/components/callpop/StoreJoinBulkResult.vue'
import StoreMappingDialog from '@/components/callpop/StoreMappingDialog.vue'
import { canRead, permittedRead } from '@/plugins/http/permittedRead'
import { httpErrorMessage } from '@/plugins/http/policy'
import { ROLE_TEXT } from '@/utils/adminRole'
import {
  CALLPOP_ACTION,
  CALLPOP_API,
  CALLPOP_PAGE_SIZE,
  CALLPOP_PATH,
  CALLPOP_WRITE_CONFIG,
  JOIN_BULK_CHUNK,
  STORE_PAGE_SIZES,
  cleanParams,
  eligibilityTags,
  isInactiveStore,
  itemsOf,
  joinBulkSummary,
  joinLockReason,
  dateOnly,
  ordersDefaultPeriod,
  pageEnvelope,
  periodParams,
  refsOf,
  stampView,
  storeAppView,
  storeMappingHistoryUrl,
  storeOrdersView,
  storeOwnerView,
  storePgContractUrl,
} from '@/utils/callpopOrg'
import { bizNoDisplay } from '@/utils/bizNumber'
import { CALLPOP_TEXT } from '@/utils/callpopText'
import { checkAuthActionAccess } from '@/utils/checkAuthActionAccess'
import { latestGuard } from '@/utils/latestGuard'
import { toNumberOrNull } from '@/utils/numberValue'
import { STORE_OPS_TEXT, canEnterStoreOps, storeOpsRoute } from '@/utils/storeOps'
import {
  PARTICIPATION_ACTION_PATH,
  PARTICIPATION_STATE,
  PARTICIPATION_WRITE_CONFIG,
  PARTNER,
  PARTNER_ACTION_PATH,
  PARTNER_LINK_ACTION,
  PARTNER_LINK_API,
  PARTNER_LINK_ROW_CHECK_CONFIG,
  PARTNER_LINK_TEXT,
  chunkStoreCds,
  participationChip,
  participationSourceText,
  partnerActionUrl,
  partnerCheckReply,
  partnerSubscribedText,
} from '@/utils/partnerLink'
import callpopOperatorsMixin from '@/mixins/callpopOperatorsMixin'
import mainVueMixin from '@/mixins/mainVueMixin'

const STORE_LIST_READ = { action: CALLPOP_ACTION.STORE_SEARCH, url: `${CALLPOP_API}/stores` }
const MAPPING_LOG_READ = { action: CALLPOP_ACTION.STORE_MAPPING_LOG, url: '', showLoading: false }
/** 일괄 참여 — 매장마다 락·트랜잭션이라 500 상한을 믿지 않고 100 씩 끊어 보낸다(서버 보고서 §10 5) */
const JOIN_BULK_URL = `${PARTNER_LINK_API}/stores/callpop/join-bulk`

/**
 * **매장 입점 현황** (화면 #2, `/v/callpop/stores`). 관리점에게는 「관리 매장 목록」이자 홈이다.
 *
 * 행 모양은 서버가 정본이고(§3-4) 화면은 그대로 그린다 — 범위도 서버가 좁힌다. 버튼은 전부 세션 액션으로
 * 가른다: PG 계약 완료일(`CallpopStorePgContract`)·일괄 참여(`CallpopJoinBulk`)는 내부만 가진 리소스라
 * 액션 없이 부르면 **403 이 아니라 401 이고 그 401 이 세션을 끊는다**(서버 보고서 §10 3).
 *
 * 참여 조건은 **행의 `eligibility`** 를 쓴다 — 운영사·관리점에게는 `…/callpop/eligibility` 리소스가 없어
 * 그 API 를 부르면 같은 함정에 빠진다(§3-9).
 */
export default {
  name: 'CallpopStoreListView',

  mixins: [mainVueMixin, callpopOperatorsMixin],

  components: { AgencyPicker, OrgHistoryTable, StoreJoinBulkResult, StoreMappingDialog },

  data() {
    return {
      text: CALLPOP_TEXT,
      /** 매장 운영 화면 문구 — 그 축(`utils/storeOps`)이 소유한다 */
      storeOpsText: STORE_OPS_TEXT,
      pageSizes: STORE_PAGE_SIZES.slice(),
      searchForm: {
        operatorCd: null,
        agencyCd: null,
        q: '',
        participation: '',
        eligibility: '',
        currPage: 0,
        pageSize: CALLPOP_PAGE_SIZE,
      },
      /** `AgencyPicker` 는 id 문자열을 다룬다 — 질의는 숫자 `agencyCd` 를 보낸다 */
      agencyId: '',
      /** 콜팝 주문 실적 기간 `[from, to]` — 기본은 이번 달 1일 ~ 오늘(KST, [ordersDefaultPeriod]) */
      ordersPeriod: ordersDefaultPeriod(Date.now()),
      items: [],
      totalCount: 0,
      loading: false,
      /** 늦게 온 응답이 최신 조건의 목록을 덮지 못하게(`utils/latestGuard`) */
      guard: latestGuard(),
      checked: [],
      activeStore: null,
      mappingVisible: false,
      historyVisible: false,
      historyItems: [],
      /** 같은 응답의 이름 사전 — 목록과 드릴다운이 같은 행을 다르게 읽지 않는다(§3-5) */
      historyRefs: null,
      historyLoading: false,
      pgVisible: false,
      pgContractDt: '',
      pgSaving: false,
      bulkVisible: false,
      bulkSummary: { requested: 0, groups: [] },
      /**
       * 「TV 조회」가 나가 있는 매장 — 그 행의 버튼만 잠근다(KT 를 같은 매장으로 두 번 부르지 않는다).
       * 배열은 갈아 끼운다(제자리 변경 없음). 목록을 다시 읽어도 매장 코드로 묻으므로 잠금이 풀리지 않는다.
       */
      checkingStoreCds: [],
    }
  },

  computed: {
    can() {
      return {
        storeAdd: checkAuthActionAccess(CALLPOP_ACTION.AGENCY_STORE_ADD),
        autoJoinRun: checkAuthActionAccess(CALLPOP_ACTION.AUTO_JOIN_RUN),
        joinBulk: checkAuthActionAccess(CALLPOP_ACTION.JOIN_BULK),
        mapping: checkAuthActionAccess(CALLPOP_ACTION.STORE_MAPPING),
        mappingLog: checkAuthActionAccess(CALLPOP_ACTION.STORE_MAPPING_LOG),
        pgContract: checkAuthActionAccess(CALLPOP_ACTION.STORE_PG_CONTRACT),
        /** 단건 참여 등록의 리소스는 참여 회차가 소유한다 — 액션 id 도 그 축이 소유한다 */
        join: checkAuthActionAccess(PARTNER_LINK_ACTION.CALLPOP_JOIN),
        /** 매장 운영 대행(R-E2) — 매장명이 「매장 관리」로 들어가는 링크가 된다(판정은 `utils/storeOps`) */
        storeOps: canEnterStoreOps(this.$store.getters['auth/auth']),
        /** KT 우리동네 가입 여부 조회 — 이 리소스도 채널 축이 소유한다(액션 id 도 그 축) */
        mytownCheck: checkAuthActionAccess(PARTNER_LINK_ACTION.MYTOWN_CHECK),
      }
    },
    /** 참여 등록 버튼의 낱말 — 참여 축의 문구는 `partnerLink` 가 소유한다(사본을 두지 않는다) */
    joinActionText() {
      return PARTNER_LINK_TEXT.joinAction
    },
    /** 「TV 조회」 버튼의 낱말 — 참여 등록과 같은 이유로 `partnerLink` 가 소유한다 */
    tvCheckActionText() {
      return PARTNER_LINK_TEXT.tvCheckAction
    },
    /** 관리점 계정에게 이 화면은 「관리 매장 목록」이다 — 매핑을 바꿀 수 없는 사람에게 「입점 현황」은 남의 말이다 */
    title() {
      return this.can.mapping ? this.text.storeListTitle : this.text.storeListTitleForAgency
    },
    denied() {
      return canRead(STORE_LIST_READ) ? '' : ROLE_TEXT.scopeReadDenied
    },
    participationFilters() {
      return [
        { value: PARTICIPATION_STATE.ACTIVE, label: PARTNER_LINK_TEXT.participationActive },
        { value: PARTICIPATION_STATE.ENDED, label: PARTNER_LINK_TEXT.participationEnded },
        { value: PARTICIPATION_STATE.NONE, label: PARTNER_LINK_TEXT.participationNone },
      ]
    },
    rows() {
      return this.items.map((row) => {
        const participation = row.participation || null
        // 조건 칸은 사유마다 태그 하나다 — 판정·문구는 `callpopOrg` 가 갖는다
        const eligibility = eligibilityTags(row.eligibility)

        return {
          raw: row,
          storeCd: row.storeCd,
          storeId: row.storeId,
          storeNm: row.storeNm,
          storeOpsRoute: storeOpsRoute(row.storeCd),
          inactive: isInactiveStore(row),
          owner: storeOwnerView(row),
          /**
           * 매장 칸으로 접은 두 사실 — 열 머리글이 없어진 자리라 **낱말을 값 앞에 단다**
           * (열을 접은 것이지 이름을 지운 것이 아니다). 주소는 길어서 제 줄을 갖고, 잘리면 `title` 에 남는다.
           */
          busiNumText: row.busiNum ? `${this.text.busiNumColumn} ${bizNoDisplay(row.busiNum)}` : '',
          storeAddr: row.storeAddr || '',
          addrTitle: row.storeAddr ? `${this.text.storeAddrColumn} ${row.storeAddr}` : '',
          mappedAt: row.mappedAt || '-',
          mappedAtDate: dateOnly(row.mappedAt) || '-',
          participationChip: participationChip(participation),
          participationSource: participationSourceText(participation && participation.source),
          /** 앱 노출 — 참여와 다른 축(콜팝 플레이스 편성 수). 참여 중인데 앱에 없으면 `warn` */
          app: storeAppView(row),
          /** 날짜·시각 두 줄 — 끊는 자리를 화면이 정한다([stampView]) */
          joinedAt: stampView(participation && participation.joinedAt),
          tvText: partnerSubscribedText(row.tv && row.tv.subscribedYn),
          regDtm: stampView(row.regDtm),
          pgContractCompletedDt: dateOnly(row.pgContractCompletedDt),
          eligibility,
          /** 콜팝 주문 실적 두 수 — 실적 기간([ordersPeriod]) 안의 유효 주문 건수·결제 금액 */
          orders: storeOrdersView(row),
          /** 이미 참여 중인 매장에는 등록 버튼이 없다 — 그 자리의 답은 해지이고 매장 상세가 소유한다 */
          joinable: !participation || participation.state !== PARTICIPATION_STATE.ACTIVE,
          joinLock: joinLockReason(row.eligibility),
        }
      })
    },
  },

  beforeDestroy() {
    this.guard.close()
  },

  created() {
    // 드릴다운으로 들어온 경우 그 관리점 필터로 연다(화면 #1 의 소속 매장 수).
    // **URL 질의는 사람이 고친다** — 숫자로 안 읽히면 없는 것으로 본다([toNumberOrNull]).
    // 맨 `Number()` 로 읽으면 `?agencyCd=abc` 가 `NaN` 이 되어 질의에 실리고 400 토스트가 뜬다
    const agencyCd = toNumberOrNull(this.$route.query.agencyCd)
    if (agencyCd != null) {
      this.searchForm.agencyCd = agencyCd
      this.agencyId = String(agencyCd)
    }
    this.searchForm.operatorCd = toNumberOrNull(this.$route.query.operatorCd)
    // 「콜팝 가입신청」 상세의 「입점 현황에서 보기」는 그 매장 번호로 검색해 연다(`?q=`)
    const q = this.$route.query.q
    if (typeof q === 'string' && q.trim()) this.searchForm.q = q.trim()

    this.loadOperators()
    this.search()
  },

  methods: {
    onOperatorFilter() {
      // 운영사가 바뀌면 앞 운영사의 관리점 필터는 뜻이 없다
      this.agencyId = ''
      this.searchForm.agencyCd = null
      this.searchFirstPage()
    },

    onAgencyFilter(id) {
      this.searchForm.agencyCd = toNumberOrNull(id)
      this.searchFirstPage()
    },

    searchFirstPage() {
      this.searchForm.currPage = 0
      this.search()
    },

    onPageChange(page) {
      this.searchForm.currPage = page - 1
      this.search()
    },

    onSizeChange(size) {
      this.searchForm.pageSize = size
      this.searchForm.currPage = 0
      this.search()
    },

    /** 목록 — 부를 수 없으면 아예 보내지 않는다([denied]) */
    async search() {
      if (this.denied) return

      const seq = this.guard.begin()
      this.loading = true
      this.checked = []

      try {
        const period = periodParams(this.ordersPeriod)
        const params = cleanParams({
          operatorCd: this.searchForm.operatorCd,
          agencyCd: this.searchForm.agencyCd,
          q: this.searchForm.q,
          participation: this.searchForm.participation,
          eligibility: this.searchForm.eligibility,
          page: this.searchForm.currPage,
          size: this.searchForm.pageSize,
          from: period.from,
          to: period.to,
        })
        const outcome = await permittedRead(Object.assign({}, STORE_LIST_READ, { params }))
        if (!this.guard.isLatest(seq)) return

        const envelope = pageEnvelope(outcome.allowed ? outcome.data : null)

        this.items = envelope.items
        this.totalCount = envelope.totalCount
      } catch (error) {
        if (!this.guard.isLatest(seq)) return

        this.items = []
        this.totalCount = 0
      } finally {
        if (this.guard.isLatest(seq)) this.loading = false
      }
    },

    onSelectionChange(rows) {
      this.checked = (rows || []).map((row) => row.storeCd).filter((cd) => cd != null)
    },

    goRegister() {
      this.$router.push({ path: CALLPOP_PATH.STORE_NEW })
    },

    openMapping(row) {
      this.activeStore = row
      this.mappingVisible = true
    },

    afterMappingSaved() {
      this.search()
    },

    async openHistory(row) {
      this.activeStore = row
      this.historyItems = []
      this.historyRefs = null
      this.historyVisible = true
      this.historyLoading = true

      try {
        const outcome = await permittedRead(
          Object.assign({}, MAPPING_LOG_READ, { url: storeMappingHistoryUrl(row.storeCd) })
        )

        this.historyItems = outcome.allowed ? itemsOf(outcome.data) : []
        this.historyRefs = outcome.allowed ? refsOf(outcome.data) : null
      } catch (error) {
        this.historyItems = []
      } finally {
        this.historyLoading = false
      }
    },

    openPgContract(row) {
      this.activeStore = row
      // 형식은 [dateOnly] 한 곳이 정한다 — 열과 입력 칸이 같은 값을 보인다(잘라내기 사본 금지)
      this.pgContractDt = dateOnly(row.pgContractCompletedDt)
      this.pgVisible = true
    },

    async savePgContract() {
      if (!this.activeStore) return

      this.pgSaving = true

      try {
        await this.$axios.put(
          storePgContractUrl(this.activeStore.storeCd),
          { completedDt: this.pgContractDt || null },
          CALLPOP_WRITE_CONFIG
        )

        this.$message({ type: 'success', message: this.text.pgContractSaved })
        this.pgVisible = false
        this.search()
      } catch (error) {
        // 400(형식)·403 은 서버 문장이 답이다 — 인터셉터가 이미 띄웠다
      } finally {
        this.pgSaving = false
      }
    },

    confirmAutoJoinRun() {
      this.$confirm(this.text.autoJoinRunConfirm, this.text.autoJoinRunAction, {
        confirmButtonText: this.text.confirmAction,
        cancelButtonText: this.text.cancelAction,
        type: 'warning',
      })
        .then(async () => {
          try {
            const response = await this.$axios.post(`${CALLPOP_API}/auto-join/run`, undefined, CALLPOP_WRITE_CONFIG)
            const result = response.data || {}

            this.$message({
              type: 'success',
              message: `${this.text.autoJoinRunAction} — ${result.scanned || 0} / ${this.text.joinBulkJoined} ${
                result.joined || 0
              }`,
            })
            this.search()
          } catch (error) {
            // 서버 문장이 답이다
          }
        })
        .catch(() => {})
    },

    /** 단건 참여 등록 — 조건 미달 400 의 한글 문장은 인터셉터가 띄운다(`callpopOrg` 주석) */
    confirmJoin(row) {
      this.$confirm(PARTNER_LINK_TEXT.joinConfirmTail, PARTNER_LINK_TEXT.joinConfirmTitle, {
        confirmButtonText: PARTNER_LINK_TEXT.joinConfirmButton,
        cancelButtonText: this.text.cancelAction,
        type: 'warning',
      })
        .then(async () => {
          try {
            await this.$axios.post(
              partnerActionUrl(row.storeCd, PARTICIPATION_ACTION_PATH, 'join'),
              undefined,
              PARTICIPATION_WRITE_CONFIG
            )
            this.search()
          } catch (error) {
            // 400(조건 미달)·409(이미 참여 중)는 서버 문장이 답이고, 화면은 목록을 다시 읽어 사실을 맞춘다
            this.search()
          }
        })
        .catch(() => {})
    },

    /**
     * 「TV 조회」 — 이 매장의 사업자번호로 KT 우리동네 가입 여부를 묻는다. 매장 정보 패널의 「조회」와 같은 요청이다
     * (경로는 [partnerActionUrl]·[PARTNER_ACTION_PATH], 답 한 줄은 [partnerCheckReply] — N 이면 KT 문안, Y 면 완료).
     *
     * 판정은 **사업자번호 단위**라 같은 사업자번호의 다른 행도 함께 바뀐다 — 응답으로 이 행을 고치지 않고 목록을 다시 읽는다.
     * 실패(400·503 의 서버 문장, 403)는 인터셉터가 이미 띄웠고 세션은 그대로다([PARTNER_LINK_ROW_CHECK_CONFIG]). 판정이
     * 바뀌지 않았으므로 다시 읽지도, KT 를 다시 부르지도 않는다 — 다시 누르는 것은 사람이다.
     */
    async checkMytown(row) {
      const storeCd = row.storeCd

      this.checkingStoreCds = this.checkingStoreCds.concat(storeCd)

      try {
        const response = await this.$axios.post(
          partnerActionUrl(storeCd, PARTNER_ACTION_PATH[PARTNER.MYTOWN], 'check'),
          undefined,
          PARTNER_LINK_ROW_CHECK_CONFIG
        )
        const reply = partnerCheckReply(response.data, PARTNER.MYTOWN)

        if (reply) this.$message({ type: reply.type, message: reply.text })
        this.search()
      } catch (error) {
        // 서버 문장이 답이다(인터셉터) — 이 행의 판정은 그대로다
      } finally {
        this.checkingStoreCds = this.checkingStoreCds.filter((cd) => cd !== storeCd)
      }
    },

    confirmJoinBulk() {
      if (this.checked.length === 0) {
        this.$message({ type: 'warning', message: this.text.joinBulkEmpty })

        return
      }

      this.$confirm(this.text.joinBulkConfirm, this.text.joinBulkTitle, {
        confirmButtonText: PARTNER_LINK_TEXT.joinConfirmButton,
        cancelButtonText: this.text.cancelAction,
        type: 'warning',
      })
        .then(() => this.joinBulk())
        .catch(() => {})
    },

    /**
     * 일괄 참여 — **100 씩 끊어 보낸다**. 묶음 나누기는 `partnerLink.chunkStoreCds` 가 이미 하는 일이라
     * 다시 적지 않고, 여러 응답을 합치는 것은 순수 함수([joinBulkSummary])가 한다.
     *
     * 한 묶음이 실패해도 앞 묶음의 결과는 사실이다 — 거기까지 모아 보여 주고 실패를 따로 말한다.
     */
    async joinBulk() {
      const chunks = chunkStoreCds(this.checked, JOIN_BULK_CHUNK)
      const results = []
      let failed = ''

      this.loading = true

      for (const storeCds of chunks) {
        try {
          const response = await this.$axios.post(
            JOIN_BULK_URL,
            { storeCds, reason: this.text.joinBulkTitle },
            CALLPOP_WRITE_CONFIG
          )

          results.push(response.data)
        } catch (error) {
          failed = httpErrorMessage(error, this.text.loadFailed)
          break
        }
      }

      this.loading = false
      this.bulkSummary = joinBulkSummary(results)
      this.bulkVisible = true
      if (failed) this.$message({ type: 'error', message: failed })
      this.search()
    },
  },
}
</script>

<style scoped>
.callpop-stores__head {
  align-items: center;
  display: flex;
  justify-content: space-between;
  margin-bottom: 8px;
}
.callpop-stores__title {
  font-size: 16px;
  margin: 0;
}
.callpop-stores__filter {
  margin-bottom: 4px;
}
.callpop-stores__operator,
.callpop-stores__agency {
  width: 180px;
}
.callpop-stores__q {
  width: 200px;
}
.callpop-stores__small {
  width: 110px;
}
.callpop-stores__period {
  width: 240px;
}
.callpop-stores__sub {
  color: #909399;
  font-size: 12px;
}
/** 참여 중인데 앱에 안 보이는 매장 — 보조 줄의 회색 대신 경고색(참여 조건 「미달」 태그와 같은 색) */
.callpop-stores__sub.callpop-stores__app-warn {
  color: #e6a23c;
}
/**
 * **날짜 조각은 끊지 않는다** — Element 의 `.el-table .cell { word-break: break-all }` 이 「2026-0 / 9-20」처럼
 * 숫자 안에서 끊는다. 끊을 자리를 고르는 일(`stampView`)은 값을 **두 줄로 앉히려고** 하는 것이고, 이 한 줄은
 * 그렇게 짧아진 조각이 그래도 끊기지 않게 막는다 — 접은 날짜 전부(등록일·입점일·PG·매핑일)가 같은 보호를 받는다.
 */
.callpop-stores__stamp {
  white-space: nowrap;
}
/**
 * **이름표 + 짧은 값 줄은 낱말 중간에서 끊지 않는다** — Element 의 `.cell{word-break:break-all}` 이
 * 「TV 입점 미확 / 인」으로 끊던 자리다(1440 dev 실측). 띄어쓰기에서만 접힌다.
 *
 * 소속 칸의 「이름 (코드)」도 같다 — 코드가 쪼개지면(「(BG0 / 001)」) 식별자로 읽히지 않는다.
 *
 * **매장명·주소 같은 자유 문장에는 걸지 않는다**: 띄어쓰기 없는 긴 이름이 칸을 넘으면 `.cell` 의
 * `overflow:hidden` 이 그것을 말없이 잘라 낸다 — 그때는 Element 의 기본(아무 데서나 접기)이 낫다.
 */
.callpop-stores__keep {
  word-break: keep-all;
}
/** 매장명 링크(매장 운영 대행) — 긴 이름은 종전 글자처럼 칸 안에서 접힌다 */
.callpop-stores__store-link {
  color: #409eff;
  text-decoration: none;
}
.callpop-stores__store-link:hover {
  text-decoration: underline;
}
/** 관리 칸 — 버튼 사이 간격과 줄바꿈을 셀이 소유한다(툴팁에 싸인 버튼도 같은 간격을 받는다) */
.callpop-stores__row-actions {
  align-items: center;
  display: flex;
  flex-wrap: wrap;
  gap: 2px 10px;
}
.callpop-stores__row-actions ::v-deep .el-button + .el-button {
  margin-left: 0;
}
/** 조건 태그 — 한 줄에 하나씩, 태그 안에서 접힌다(잘리지 않는다) */
.callpop-stores__reason + .callpop-stores__reason {
  margin-top: 2px;
}
.callpop-stores__reason ::v-deep .el-tag {
  height: auto;
  line-height: 1.5;
  max-width: 100%;
  padding: 1px 6px;
  white-space: normal;
  word-break: keep-all;
}
/** 긴 주소가 칸을 늘리지 않게 한 줄로 자른다 — 잘린 값은 `title` 로 남는다(정보를 없애지 않는다) */
.callpop-stores__clip {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.callpop-stores__pg {
  width: 100%;
}
.callpop-stores__hint {
  color: #909399;
  font-size: 12px;
  margin: 8px 0 0;
}
</style>
